import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, FileText, Eye, Edit } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import MarkdownRenderer from './MarkdownRenderer';
import Layout from './Layout';

const TermsManagement: React.FC = () => {
  const navigate = useNavigate();
  const { themeClasses } = useTheme();
  const [termsContent, setTermsContent] = useState('');
  const [privacyPolicyContent, setPrivacyPolicyContent] = useState('');
  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>('terms');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showTermsPreview, setShowTermsPreview] = useState(false);
  const [showPrivacyPreview, setShowPrivacyPreview] = useState(false);

  useEffect(() => {
    fetchTerms();
    fetchPrivacyPolicy();
  }, []);

  const fetchTerms = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/terms');
      setTermsContent(response.data.terms || '');
    } catch (error) {
      console.error('Error fetching terms:', error);
      toast.error('Failed to load terms and conditions');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPrivacyPolicy = async () => {
    try {
      const response = await api.get('/admin/privacy-policy');
      setPrivacyPolicyContent(response.data.privacyPolicy || '');
    } catch (error) {
      console.error('Error fetching privacy policy:', error);
      toast.error('Failed to load privacy policy');
    }
  };

  const handleSaveTerms = async () => {
    if (!termsContent.trim()) {
      toast.error('Terms and conditions content cannot be empty');
      return;
    }

    try {
      setIsSaving(true);
      await api.put('/admin/terms', { terms: termsContent });
      toast.success('Terms and conditions updated successfully!');
    } catch (error) {
      console.error('Error updating terms:', error);
      toast.error('Failed to update terms and conditions');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePrivacyPolicy = async () => {
    if (!privacyPolicyContent.trim()) {
      toast.error('Privacy policy content cannot be empty');
      return;
    }

    try {
      setIsSaving(true);
      await api.put('/admin/privacy-policy', { privacyPolicy: privacyPolicyContent });
      toast.success('Privacy policy updated successfully!');
    } catch (error) {
      console.error('Error updating privacy policy:', error);
      toast.error('Failed to update privacy policy');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout title="Terms Management" showBackButton onBack={() => navigate('/admin/users')}>
        <div className="p-4">
          <div className={`${themeClasses.card} p-8 text-center rounded-lg`}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
            <p className={`${themeClasses.textSecondary} mt-4`}>Loading terms and conditions...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Terms Management" showBackButton onBack={() => navigate('/admin/users')}>

      {/* Content */}
      <div className="p-4">
        <div className={`${themeClasses.card} rounded-lg`}>
          {/* Tabs */}
          <div className="flex border-b mb-4">
            <button
              onClick={() => setActiveTab('terms')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'terms'
                  ? `${themeClasses.text} border-b-2 border-primary-500`
                  : `${themeClasses.textSecondary}`
              }`}
            >
              Terms and Conditions
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'privacy'
                  ? `${themeClasses.text} border-b-2 border-primary-500`
                  : `${themeClasses.textSecondary}`
              }`}
            >
              Privacy Policy
            </button>
          </div>

          {activeTab === 'terms' ? (
            <>
              <div className="flex items-center mb-4">
                <FileText className={`w-5 h-5 ${themeClasses.textSecondary} mr-2`} />
                <h2 className={`text-lg font-semibold ${themeClasses.text}`}>Terms and Conditions</h2>
              </div>
              
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className={`block text-sm font-medium ${themeClasses.textSecondary}`}>
                    Content (Markdown supported)
                  </label>
                  <button
                    onClick={() => setShowTermsPreview(!showTermsPreview)}
                    className={`${themeClasses.buttonSecondary} px-3 py-1.5 rounded-lg flex items-center text-sm`}
                  >
                    {showTermsPreview ? (
                      <>
                        <Edit className="w-4 h-4 mr-1.5" />
                        Edit
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1.5" />
                        Preview
                      </>
                    )}
                  </button>
                </div>
                {showTermsPreview ? (
                  <div className={`${themeClasses.card} p-4 h-96 overflow-y-auto border ${themeClasses.border} rounded-lg`}>
                    <MarkdownRenderer content={termsContent || '*No content to preview*'} />
                  </div>
                ) : (
                  <textarea
                    value={termsContent}
                    onChange={(e) => setTermsContent(e.target.value)}
                    className={`${themeClasses.input} w-full h-96 resize-none rounded-lg px-3 py-2 font-mono text-sm`}
                    placeholder="Enter the terms and conditions content here... (Markdown syntax supported)"
                  />
                )}
                <p className={`text-xs ${themeClasses.textSecondary} mt-2`}>
                  💡 Tip: Use Markdown syntax for formatting (headers, lists, links, etc.)
                </p>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-400">Important</h3>
                    <div className="mt-2 text-sm text-yellow-300">
                      <p>
                        Changes to the terms and conditions will affect all new pitch creations. 
                        Users will be required to accept these terms before creating pitches.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => navigate('/admin/users')}
                  className={`${themeClasses.buttonSecondary} px-4 py-2 rounded-lg`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTerms}
                  disabled={isSaving}
                  className={`${themeClasses.button} px-4 py-2 rounded-lg flex items-center ${
                    isSaving ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center mb-4">
                <FileText className={`w-5 h-5 ${themeClasses.textSecondary} mr-2`} />
                <h2 className={`text-lg font-semibold ${themeClasses.text}`}>Privacy Policy</h2>
              </div>
              
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className={`block text-sm font-medium ${themeClasses.textSecondary}`}>
                    Content (Markdown supported)
                  </label>
                  <button
                    onClick={() => setShowPrivacyPreview(!showPrivacyPreview)}
                    className={`${themeClasses.buttonSecondary} px-3 py-1.5 rounded-lg flex items-center text-sm`}
                  >
                    {showPrivacyPreview ? (
                      <>
                        <Edit className="w-4 h-4 mr-1.5" />
                        Edit
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1.5" />
                        Preview
                      </>
                    )}
                  </button>
                </div>
                {showPrivacyPreview ? (
                  <div className={`${themeClasses.card} p-4 h-96 overflow-y-auto border ${themeClasses.border} rounded-lg`}>
                    <MarkdownRenderer content={privacyPolicyContent || '*No content to preview*'} />
                  </div>
                ) : (
                  <textarea
                    value={privacyPolicyContent}
                    onChange={(e) => setPrivacyPolicyContent(e.target.value)}
                    className={`${themeClasses.input} w-full h-96 resize-none rounded-lg px-3 py-2 font-mono text-sm`}
                    placeholder="Enter the privacy policy content here... (Markdown syntax supported)"
                  />
                )}
                <p className={`text-xs ${themeClasses.textSecondary} mt-2`}>
                  💡 Tip: Use Markdown syntax for formatting (headers, lists, links, etc.)
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => navigate('/admin/users')}
                  className={`${themeClasses.buttonSecondary} px-4 py-2 rounded-lg`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePrivacyPolicy}
                  disabled={isSaving}
                  className={`${themeClasses.button} px-4 py-2 rounded-lg flex items-center ${
                    isSaving ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default TermsManagement; 